import { create } from 'zustand';
import { onSessionChange } from './session-read';
import { safeGetItem, safeSetItem } from '../utils/storage';

// ============================================
// Constants
// ============================================

const COUNTDOWN_LAST_SHOWN_KEY = 'trial_countdown_last_shown';
const EXPIRED_DISMISSED_KEY = 'trial_expired_dismissed';
const COUNTDOWN_THRESHOLD_DAYS = 3;

// ============================================
// Type Definitions
// ============================================

type ActiveModal = 'countdown' | 'expired' | null;

interface TrialModalState {
  activeModal: ActiveModal;
  daysRemaining: number | null;
}

interface TrialModalActions {
  evaluateTrialStatus: (params: {
    subscription: { platform?: string; expires_at?: string } | null;
    plan: Record<string, unknown> | null;
    celebrationIsOpen: boolean;
  }) => void;
  dismissCountdown: () => void;
  dismissExpired: () => void;
  reset: () => void;
}

type TrialModalStore = TrialModalState & TrialModalActions;

// ============================================
// Helpers
// ============================================

function getTodayDateString(): string {
  return new Date().toISOString().split('T')[0];
}

function getDaysRemaining(expiresAt: string): number {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function wasCountdownShownToday(): boolean {
  const lastShown = safeGetItem(COUNTDOWN_LAST_SHOWN_KEY);
  return lastShown === getTodayDateString();
}

function wasExpiredDismissed(): boolean {
  return safeGetItem(EXPIRED_DISMISSED_KEY) === 'true';
}

// ============================================
// Store Implementation
// ============================================

export const useTrialModalStore = create<TrialModalStore>((set) => ({
  activeModal: null,
  daysRemaining: null,

  evaluateTrialStatus: ({ subscription, plan, celebrationIsOpen }) => {
    // Don't show if a celebration modal is open — defer
    if (celebrationIsOpen) return;

    // Case 1: Active trial subscription nearing expiry
    if (subscription?.platform === 'trial' && subscription.expires_at) {
      const days = getDaysRemaining(subscription.expires_at);

      if (days <= COUNTDOWN_THRESHOLD_DAYS && days > 0 && !wasCountdownShownToday()) {
        set({ activeModal: 'countdown', daysRemaining: days });
        return;
      }
    }

    // Case 2: Trial expired — no active subscription, but user was a trial user
    if (
      subscription === null &&
      plan &&
      (plan as Record<string, unknown>).subscription_status === 'trial' &&
      !wasExpiredDismissed()
    ) {
      set({ activeModal: 'expired', daysRemaining: 0 });
      return;
    }
  },

  dismissCountdown: () => {
    safeSetItem(COUNTDOWN_LAST_SHOWN_KEY, getTodayDateString());
    set({ activeModal: null, daysRemaining: null });
  },

  dismissExpired: () => {
    safeSetItem(EXPIRED_DISMISSED_KEY, 'true');
    set({ activeModal: null, daysRemaining: null });
  },

  reset: () => {
    set({ activeModal: null, daysRemaining: null });
  },
}));

// A trial modal raised for one account closes when the session changes (#940).
onSessionChange(() => useTrialModalStore.getState().reset());
